import { dollars } from "@loot-os/api-contract/shopifyRuleBoxForm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PURCHASE_STATUS, SOLD_OUT_PURCHASE, TRANSFER_REASON, TRANSFER_STATUS } from "./labels";
import { TABLE, TD, TH } from "./tableStyles";
import { useTRPC } from "./trpc";

/**
 * What a purchase turned into: its ship order in Shopify (opened in the admin), the store credit
 * it sent the customer, or both when a box got cheaper and the rest went to store credit.
 */
function PurchaseResult({
  purchase,
  shopDomain,
}: {
  purchase: {
    status: string;
    shipOrderGid: string | null;
    storeCreditCents: number;
  };
  shopDomain: string;
}) {
  const shipOrderId = purchase.shipOrderGid?.split("/").pop();
  const shipping = purchase.status === "claimed";
  const credit = purchase.storeCreditCents > 0;
  if (!shipping && !credit) return <s-text color="subdued">—</s-text>;
  return (
    <s-stack gap="small-200">
      {shipping &&
        (shipOrderId ? (
          <s-link href={`https://${shopDomain}/admin/orders/${shipOrderId}`} target="_top">
            Ship order
          </s-link>
        ) : (
          <s-text>Ship order being created</s-text>
        ))}
      {credit && <s-text>{dollars(purchase.storeCreditCents)} store credit</s-text>}
    </s-stack>
  );
}

/**
 * Box units bought at checkout, with the item each revealed and what it turned into, and the
 * store credit transfers still waiting to land.
 */
export function PurchasesScreen({ shopDomain }: { shopDomain: string }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const purchases = useQuery(trpc.purchases.list.queryOptions());
  const transfers = useQuery(trpc.purchases.transfers.queryOptions());
  const retry = useMutation(
    trpc.purchases.retryTransfer.mutationOptions({
      onSuccess: (result) => {
        shopify.toast.show(
          result.status === "applied"
            ? "Store credit applied."
            : (result.lastError ?? "Retry failed."),
          { isError: result.status !== "applied" },
        );
        void queryClient.invalidateQueries({ queryKey: trpc.purchases.transfers.queryKey() });
      },
    }),
  );

  return (
    <>
      {(transfers.data ?? []).length > 0 && (
        <s-stack gap="base">
          <s-heading>Store credit that hasn't reached customers</s-heading>
          <s-paragraph>
            Loot OS couldn't add this store credit to the customer's account yet. It tries again on
            its own; click "Try again" to retry now.
          </s-paragraph>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={TH}>Why</th>
                <th style={TH}>Amount</th>
                <th style={TH}>Status</th>
                <th style={TH}>Last error</th>
                <th style={TH}>
                  <s-text accessibilityVisibility="exclusive">Actions</s-text>
                </th>
              </tr>
            </thead>
            <tbody>
              {(transfers.data ?? []).map((t) => (
                <tr key={t.id}>
                  <td style={TD}>{TRANSFER_REASON[t.reason]}</td>
                  <td style={TD}>{dollars(t.amountCents)}</td>
                  <td style={TD}>
                    <s-badge tone={TRANSFER_STATUS[t.status].tone}>
                      {TRANSFER_STATUS[t.status].label}
                    </s-badge>
                  </td>
                  <td style={TD}>{t.lastError ?? ""}</td>
                  <td style={TD}>
                    <s-button
                      disabled={retry.isPending || undefined}
                      onClick={() => retry.mutate({ id: t.id })}
                    >
                      Try again
                    </s-button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </s-stack>
      )}
      <s-stack gap="base">
        <s-heading>Boxes your customers bought</s-heading>
        <s-paragraph>
          One row per box bought, newest first, with the item it revealed and what the customer
          took: the item shipped on its own $0 order, or its value as store credit. Each box order's
          note in Shopify says the same.
        </s-paragraph>
        {purchases.isLoading ? (
          <s-paragraph>Loading purchases…</s-paragraph>
        ) : (purchases.data ?? []).length === 0 ? (
          <s-paragraph color="subdued">No one has bought a box yet.</s-paragraph>
        ) : (
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={TH}>Order</th>
                <th style={TH}>Box</th>
                <th style={TH}>Paid</th>
                <th style={TH}>Item revealed</th>
                <th style={TH}>Status</th>
                <th style={TH}>Result</th>
              </tr>
            </thead>
            <tbody>
              {(purchases.data ?? []).map((p) => {
                const status = p.soldOut ? SOLD_OUT_PURCHASE : PURCHASE_STATUS[p.status];
                return (
                  <tr key={p.id}>
                    <td style={TD}>{p.orderName}</td>
                    <td style={TD}>{p.boxName}</td>
                    <td style={TD}>{dollars(p.paidPriceCents)}</td>
                    <td style={TD}>
                      {p.item ? (
                        <s-stack gap="small-200">
                          <s-text>{p.item.name}</s-text>
                          <s-text color="subdued">Worth {dollars(p.item.valueCents)}</s-text>
                        </s-stack>
                      ) : (
                        <s-text color="subdued">—</s-text>
                      )}
                    </td>
                    <td style={TD}>
                      <s-badge tone={status.tone}>{status.label}</s-badge>
                    </td>
                    <td style={TD}>
                      <PurchaseResult purchase={p} shopDomain={shopDomain} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </s-stack>
    </>
  );
}
